import BigNumber from 'bignumber.js'
import { useQuery } from 'react-query'
import { toast } from 'react-toastify'
import { quantityToNumber } from 'utils'

type Data = {
  productId: string
  sequence: string
  items: any[]
  salePrice: number
  icmsPercent: number
  ipiPercent: number
  stPercent: number
  quantity: number
  freightValue: number
}

type Output = {
  icmsValue: number
  ipiValue: number
  stValue: number
}

function calculateFreightRatio(input: any) {
  const { itemTotalValue, totalItems } = input
  return BigNumber(itemTotalValue).div(totalItems || itemTotalValue)
}

function calculateItemIpi(input: any) {
  const { item, itemFreightValue, itemTotalValue } = input
  const ipiRatio = BigNumber(item.ipiPercent).times(0.01)
  return ipiRatio.times(BigNumber(itemTotalValue).plus(itemFreightValue))
}

function calculateItemIcms(input: any) {
  const { item, itemFreightValue, itemTotalValue } = input
  const icmsRatio = BigNumber(item.icmsPercent).times(0.01)
  return icmsRatio.times(BigNumber(itemTotalValue).plus(itemFreightValue))
}

function calculateItemSt(input: any) {
  const { item, icms } = input
  const stRatio = BigNumber(item.stPercent).times(0.01)
  return stRatio.times(icms)
}

function calculateItemTaxes(input: any) {
  const { item, freightValue, totalItems, itemTotalValue } = input

  const freightRatio = calculateFreightRatio({
    itemTotalValue,
    totalItems
  })
  const itemFreightValue = BigNumber(freightRatio).times(freightValue)

  const ipi = calculateItemIpi({ item, itemFreightValue, itemTotalValue })
  const icms = calculateItemIcms({ item, itemFreightValue, itemTotalValue })
  const st = calculateItemSt({ item, icms })

  return { ipi, icms, st }
}

const loadOrderProductTaxes = (data: Data): Promise<Output> => {
  const {
    salePrice,
    quantity,
    icmsPercent,
    ipiPercent,
    stPercent,
    items,
    sequence,
    freightValue
  } = data

  const itemTotalValue = BigNumber(salePrice).times(quantity)

  const totalItems = items.reduce((acc: BigNumber, cur: any) => {
    const itemQuantity = quantityToNumber(cur.quantity)
    if (cur.sequence !== sequence)
      return acc.plus(BigNumber(cur.salePrice).times(itemQuantity))
    else return acc.plus(itemTotalValue)
  }, BigNumber(0))

  const { icms, ipi, st } = calculateItemTaxes({
    item: { icmsPercent, ipiPercent, stPercent },
    freightValue,
    totalItems: totalItems.toNumber(),
    itemTotalValue
  })

  const icmsValue = icms.toNumber()
  const ipiValue = ipi.toNumber()
  const stValue = st.toNumber()

  return new Promise((resolve) => resolve({ icmsValue, ipiValue, stValue }))
}

export function useLoadOrderProductTaxes(query: Data) {
  return useQuery(
    ['orderProductTaxes', query],
    () => loadOrderProductTaxes(query),
    {
      enabled:
        !!query.productId &&
        !!query.sequence &&
        query.icmsPercent >= 0 &&
        query.ipiPercent >= 0 &&
        query.stPercent >= 0 &&
        query.salePrice >= 0 &&
        query.quantity > 0,

      onError: () =>
        toast.error('Não foi possível carregar os impostos do produto!', {
          toastId: 'orderProductTaxes'
        })
    }
  )
}
